import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import {
  getMerchantBillingStats,
  type CurrencyVolume,
  type MerchantBillingStats,
} from "../api/billing";
import { brand } from "../theme";

const ZERO_DECIMAL = new Set(["PYG", "JPY", "KRW", "VND", "CLP"]);

export function formatCurrencyAmount(amount: string, currency: string): string {
  const raw = Number(amount);
  if (!Number.isFinite(raw)) return `${amount} ${currency}`;
  const value = ZERO_DECIMAL.has(currency) ? Math.round(raw) : raw;
  return new Intl.NumberFormat("es-PY", {
    minimumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
    maximumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
  }).format(value);
}

export function periodOptions(count = 6): { value: string; label: string }[] {
  const now = new Date();
  const opts: { value: string; label: string }[] = [];
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const value = `${year}-${String(month).padStart(2, "0")}`;
    const label = d.toLocaleString("es-PY", {
      month: "long",
      year: "numeric",
      timeZone: "UTC",
    });
    opts.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
  }
  return opts;
}

interface Props {
  merchantId: string;
}

export function MerchantBillingStatsView({ merchantId }: Props) {
  const options = useMemo(() => periodOptions(6), []);
  const [period, setPeriod] = useState(options[0].value);
  const [stats, setStats] = useState<MerchantBillingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getMerchantBillingStats(merchantId, period)
      .then((s) => {
        if (!cancelled) setStats(s);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "No pudimos cargar la actividad");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [merchantId, period]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          Actividad del período
        </Typography>
        <TextField
          select
          size="small"
          label="Período"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          {options.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <CountTile label="Aprobadas" value={stats?.approvedCount} loading={loading} tone="success" />
        <CountTile label="Rechazadas" value={stats?.rejectedCount} loading={loading} tone="error" />
        <CountTile label="Pendientes" value={stats?.pendingCount} loading={loading} tone="warning" />
      </Stack>

      <Box>
        <Typography variant="overline" color="text.secondary">
          Volumen aprobado por moneda
        </Typography>
        {loading ? (
          <Skeleton width={240} height={36} />
        ) : stats && (stats.volumes?.length ?? 0) > 0 ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {(stats.volumes ?? []).map((v) => (
              <VolumeChip key={v.currency} volume={v} />
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Sin transacciones aprobadas en este período.
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

function CountTile({
  label,
  value,
  loading,
  tone,
}: {
  label: string;
  value: number | undefined;
  loading: boolean;
  tone: "success" | "error" | "warning";
}) {
  const colorMap = {
    success: "#1b8c4f",
    error: "#b1391a",
    warning: "#a37500",
  };
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={60} height={42} />
      ) : (
        <Typography variant="h4" fontWeight={700} sx={{ color: colorMap[tone] }}>
          {value ?? 0}
        </Typography>
      )}
    </Paper>
  );
}

function VolumeChip({ volume }: { volume: CurrencyVolume }) {
  return (
    <Chip
      label={
        <Box component="span" sx={{ fontFamily: "monospace" }}>
          <strong>{volume.currency}</strong>{" "}
          {formatCurrencyAmount(volume.approvedVolume, volume.currency)}{" "}
          <Typography component="span" variant="caption" color="text.secondary">
            · {volume.approvedCount} tx
          </Typography>
        </Box>
      }
      sx={{ bgcolor: brand.navy, color: "white", "& *": { color: "white" } }}
    />
  );
}
